import React from 'react';

import { apolloHooks, LazyQueryHookOptions, OperationVariables } from '@nxte-nl/apollo-hooks';
import { callbackHooks } from '@nxte-nl/callback-hooks';

import { GiftcardPackageGroupWithShipmentId } from '../../components/Basket/Basket';
import { cartQuantity } from '../../queries/cartQuantity.gql';
import { ItemToEdit, ItemToDelete } from '../../types/Basket';
import {
  BasketDeleteItemModalMessages,
  BasketMessages,
  BasketSignals,
  GlobalMessages,
  GlobalSignals,
} from '../../types/Messages';
import { OnModalOpen } from '../../types/Modals';
import { CartResponse } from '../../types/apiCompoundCommon';
import {
  GiftcardOrderItem,
  PackageOrderItem,
  GiftcardPackage,
  GiftCardVariationType,
  GiftcardProductVariation,
  GiftcardProduct,
} from '../../types/apiTypes';
import { messageHooks } from '../messageHooks/messageHooks';
import { cartV2Hooks } from '../queryHooks/cartV2Hooks/cartV2Hooks';

export const orderHooks = {
  useHandleGetCart(quantityOnly?: boolean) {
    const setCart = messageHooks.useNext(BasketMessages.Cart);
    const setIsCartSet = messageHooks.useNext(BasketMessages.isCartSet);
    const { executeRequest, loading } = cartV2Hooks.useCartV2Query(setCart, setIsCartSet);

    const handleGetCart = React.useCallback(() => {
      if (!quantityOnly) {
        executeRequest({});
      }
    }, [executeRequest, quantityOnly]);

    callbackHooks.useEffectCallback(handleGetCart);

    return {
      handleGetCart,
      loading,
      executeRequest,
    };
  },

  useGetCartQuantityCallback(executeRequest: (options?:
    Partial<LazyQueryHookOptions<CartResponse | null,
    OperationVariables>> | undefined) => Promise<unknown>) {
    const handleGetCartQuantity = React.useCallback(() => {
      executeRequest({});
    }, [executeRequest]);
    return { handleGetCartQuantity };
  },

  useHandleGetCartQuantity() {
    const setCartQuantity = messageHooks.useNext(GlobalMessages.CartQuantity);
    const [executeRequest, { loading }] = apolloHooks.useLazyQuery(
      cartQuantity,
      {
        onCompleted: setCartQuantity,
      },
    );

    const { handleGetCartQuantity } = orderHooks.useGetCartQuantityCallback(executeRequest);

    callbackHooks.useEffectCallback(handleGetCartQuantity);
    return {
      handleGetCartQuantity,
      loading,
      executeRequest,
    };
  },

  useOrderItems(response: CartResponse | null, isRedeem?: boolean, quantityOnly?: boolean) {
    const {
      handleGetCart,
      loading,
      executeRequest,
    } = orderHooks.useHandleGetCart(quantityOnly);

    const orderItems = React.useMemo(() => {
      if (!response || !response.data || isRedeem) {
        return;
      }
      const { packagesGroups, shipments } = response.data;
      const mappedShipmentsIds: {
        [key: string]: string;
      } = {};

      shipments?.forEach(shipment => {
        shipment.packages.forEach(p => {
          mappedShipmentsIds[p.id] = shipment.id;
        });
      });

      const packages =
        shipments?.reduce((accumulator, shipment) => [...accumulator,
          ...shipment.packages.map(el => ({
            ...el,
            deliveryAddress: shipment.deliveryAddress,
            deliveryDate: shipment.deliveryDate,
            variations: (shipment.packages[0] as GiftcardPackage).
              giftcard_v1?.variation.product?.variations || [],
            variationsToShow: ((shipment.packages[0] as GiftcardPackage).
              giftcard_v1?.variation.product as GiftcardProduct)?.variationsToShow || [],
            personalNoteToPrefill: (shipment.packages[0] as GiftcardPackage).message?.text,
          }))], [])
        ?? null;

      return packagesGroups?.map((packageGroup) => {
        const giftcardPackage = packages?.find(
          (cardPackage) => cardPackage.id === packageGroup.packageId,
        );

        const isGiftCardPackage = giftcardPackage?.__typename === 'GiftcardPackage';

        let packageGiftcard: GiftcardOrderItem | PackageOrderItem | null = null;
        if (isGiftCardPackage) {
          packageGiftcard = (giftcardPackage as GiftcardPackage).giftcard_v1 ?? null;
        }
        return quantityOnly ? {
          quantity: packageGiftcard?.qty,
        } as GiftcardPackageGroupWithShipmentId : {
          cardsPreviews: packageGroup.cardsPreviews,
          tag: packageGroup.tag,
          title: packageGiftcard?.variation.product.title,
          price: packageGiftcard?.variation.price,
          shipmentId: mappedShipmentsIds[packageGroup.packageId],
          packageId: packageGroup.packageId,
          receiverTitle: packageGroup.receiverTitle,
          receiverDescription: packageGroup.receiverDescription,
          totalPrice: packageGroup.totalPrice,
          quantity: packageGiftcard?.qty,
          deliveryAddress: giftcardPackage?.deliveryAddress ?? null,
          deliveryDate: giftcardPackage?.deliveryDate,
          deliveryType: (packageGiftcard?.variation as GiftcardProductVariation).variationType,
          variations: giftcardPackage?.variations || [],
          variationsToShow: giftcardPackage?.variationsToShow || [],
          personalNoteToPrefill: giftcardPackage?.personalNoteToPrefill || '',
          ...(isGiftCardPackage && {
            messagePrice: (giftcardPackage as GiftcardPackage)?.message?.variation.price,
            messageTitle: (giftcardPackage as GiftcardPackage)?.message?.variation.title,
            wrapperPrice: (giftcardPackage as GiftcardPackage)?.wrapper?.variation.price,
            wrapperTitle: (giftcardPackage as GiftcardPackage)?.wrapper?.variation.title,
            digitalTitle: packageGiftcard?.__typename === 'GiftcardOrderItem'
              && packageGiftcard.variation.
                variationType === GiftCardVariationType.DIGITAL
              ? (packageGiftcard as GiftcardOrderItem).variation.title : undefined,
          }),
        };
      });
    }, [isRedeem, quantityOnly, response]);

    const orderPrice = React.useMemo(() => response?.data?.totals, [response]);

    return {
      loading,
      orderItems,
      orderPrice,
      getOrderItems: executeRequest,
      handleGetCart,
    };
  },
  useOrderItemsQuantity(response: CartResponse | null, isRedeem: boolean) {
    const { orderItems } = orderHooks.useOrderItems(response, isRedeem, true);

    const orderItemsQuantity = orderItems?.reduce((q, { quantity }) => q + Number(quantity), 0);

    return Number(orderItemsQuantity) > 0
      ? orderItemsQuantity
      : undefined;
  },
  useModals(
    onModalOpen: OnModalOpen,
    onModalOpenBasketDeleteItem: OnModalOpen,
  ) {
    const setItemToEdit = messageHooks.useNext(BasketMessages.ItemToEdit);
    const setItemToDelete = messageHooks.useNext(BasketDeleteItemModalMessages.ItemToDelete);

    const onEditItem = React.useCallback((itemToEdit: ItemToEdit) => {
      setItemToEdit(itemToEdit);
      onModalOpen(false);
    }, [setItemToEdit, onModalOpen]);

    const onDeleteItem = React.useCallback((itemToDelete: ItemToDelete) => {
      setItemToDelete(itemToDelete);
      onModalOpenBasketDeleteItem(false);
    }, [setItemToDelete, onModalOpenBasketDeleteItem]);

    return { onEditItem, onDeleteItem };
  },
  useUpdateOrderItem(handleGetCart: () => void) {
    messageHooks.useSubscribeSignal(
      BasketSignals.OnUpdateBasketItem,
      handleGetCart,
    );
  },
  useUpdateOrderQuantity(getOrderQuantity: (params: unknown) => void) {
    const handleOrderQuantity = React.useCallback(() => getOrderQuantity({}), [getOrderQuantity]);
    messageHooks.useSubscribeSignal(
      GlobalSignals.OnUpdateCartQuantity,
      handleOrderQuantity,
    );
  },

};
